import { useMemo } from "react";
import { useQuery } from "./dataClient.js";
import { sumSeries } from "./renderers.js";

// Tenant baseline for composition lift (see computeLift in compositionModel.js).
// Fetches the org-wide prompt total plus each profiled dim's org-wide member
// ranking, so a caller can turn any shown member's share-of-scope into a
// share-of-org comparison ("this endpoint is 1.6× more Claude-heavy than the
// org at large"). Fixed hook shape — one totals query + SLOTS dim queries — so
// hook order never changes with the dim count. Disabled at the tenant scope,
// where lift would compare a scope to itself. The tenant-level queries it issues
// are the same ones the sidebar/overview already fetch, so they usually resolve
// from cache.

const SLOTS = 3;
const BASE_LIMIT = 50; // deep enough that a scope's top members are usually ranked org-wide too

/**
 * Build the per-dim {memberId → org activity} lookup from the fetched slots.
 * A slot with no data (disabled at tenant, or still loading) yields an empty
 * inner map so callers can compute lift the moment entries arrive. Pure —
 * unit-tested in useScopeBaseline.test.js.
 * @param {Array<{dimKey:string|null, data:{entries?:Array}|undefined}>} slots
 * @returns {Map<string, Map<string, number>>}
 */
export function buildBaseByDim(slots) {
  const baseByDim = new Map();
  for (const s of slots) {
    if (!s.dimKey) continue;
    const m = new Map();
    for (const e of s.data?.entries || []) m.set(String(e.id), Number(e.activity) || 0);
    baseByDim.set(s.dimKey, m);
  }
  return baseByDim;
}

export function useScopeBaseline(dimKeys, range, enabled = true) {
  const { data: bundle } = useQuery(
    "getOverviewActivityBundle",
    { scopePath: [], bucketCount: 1, topN: 1, range },
    { enabled },
  );
  const baseTotal = sumSeries(bundle?.prompts?.total);

  const slots = [];
  for (let i = 0; i < SLOTS; i++) {
    const dimKey = dimKeys[i] || null;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data } = useQuery(
      "getDimensionEntries",
      { dimKey: dimKey || "endpoints", parentScopePath: [], limit: BASE_LIMIT, range, sparkline: false },
      { enabled: enabled && Boolean(dimKey) },
    );
    slots.push({ dimKey, data });
  }

  return useMemo(() => ({ baseTotal, baseByDim: buildBaseByDim(slots) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseTotal, ...slots.map((s) => s.data)]);
}
